import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-hot-toast";
import apiService, { handleError } from "../../Common/ApiCommon/ApiService";
import icons from "../../../assets/images/constantIcons";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';


const EditStaffForm = ({ editPopup, onClose, staffId, fetchStaffDetails }) => {
  const [staffData, setStaffData] = useState(null);
  const [roles, setRoles] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false); // ✅ Prevent multiple updates
  const [location, setLocation] = useState([]);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Fetch Roles Once
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await apiService.get("roles");
        setRoles(response.roles);
      } catch (error) {
        toast.error("Failed to fetch roles.");
      }
    };
    fetchRoles();
  }, []);

  // ✅ Fetch Staff Data by ID
  const fetchStaffData = async () => {
    try {
      const response = await apiService.get(`/staff_users/${staffId}`);
      setStaffData(response.data); // ✅ Store latest data in state
    } catch (error) {
      toast.error("Failed to load staff data.");
    }
  };

  useEffect(() => {
    if (staffId) {
      fetchStaffData();
    }
  }, [staffId]);

  // Use Formik with `enableReinitialize`
  const formik = useFormik({
    enableReinitialize: true, // ✅ Allows dynamic form updates
    initialValues: {
      full_name: staffData?.full_name || "",
      mobile_no: staffData?.mobile_no || "",
      email: staffData?.email || "",
      base_salary: staffData?.base_salary || "",
      joining_date: staffData?.joining_date || "",
      role_id:
        roles.find((role) => role.name === staffData?.role?.name)?.id || "",
      location: staffData?.location || "",
    },

    validationSchema: Yup.object({
      full_name: Yup.string()
        .min(3, "Full Name must be at least 3 characters")
        .required("Full Name is required"),
      mobile_no: Yup.string()
        .required("Mobile is required"),
      email: Yup.string()
        .email("Invalid email address")
        .required("Email is required"),
      base_salary: Yup.number()
        .min(0, "Base Salary must be a positive number")
        .required("Base Salary is required"),
      joining_date: Yup.string().required("Joining Date is required"),
      role_id: Yup.string().required("Role is required"),
      location: Yup.string().required("Location is required"),
      password: Yup.string()
        .min(6, "Password must be at least 6 characters")
        .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
        .matches(/[a-z]/, "Password must contain at least one lowercase letter")
        .matches(/\d/, "Password must contain at least one number")
        .matches(
          /[!@#$%^&*(),.?":{}|<>]/,
          "Password must contain at least one special character"
        ),
      password_confirmation: Yup.string()
        .oneOf([Yup.ref("password"), null], "Passwords must match")
    }),

    onSubmit: async (values) => {
      setIsUpdating(true);

      try {
        const formData = new FormData();
        formData.append("staff_user[full_name]", values.full_name);
        formData.append("staff_user[mobile_no]", values.mobile_no);
        formData.append("staff_user[base_salary]", values.base_salary);
        formData.append("staff_user[joining_date]", values.joining_date);
        formData.append("staff_user[location]", values.location);
        formData.append("staff_user[role_id]", values.role_id);
        formData.append("staff_user[password]", values.password);
        formData.append("staff_user[password_confirmation]", values.password_confirmation);

        const response = await apiService.put(
          `/staff_users/${staffId}`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );
        if (response.status === 200) {
          toast.success(response.message, { duration: 2000 });
          onClose();
          fetchStaffDetails();
        }
      } catch (errors) {
        handleError(errors);
      } finally {
        setIsUpdating(false);
      }
    },
  });

  // locations get api
  const fetchLocations = async () => {
    try {
      const response = await apiService.get("/locations");
      setLocation(response.data);
    } catch (errors) {
      handleError(errors)
    }
  };
  useEffect(() => {
    fetchLocations();
  }, []);

  return (
    editPopup && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-[350px] md:w-[460px] relative">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Edit Staff</h2>
            <div
              className="absolute flex justify-center items-center right-3 bg-green50 rounded-full p-2 cursor-pointer w-[32px] h-[32px]"
              onClick={onClose}
            >
              <icons.IoClose className="text-green700 " />
            </div>
          </div>

          <form onSubmit={formik.handleSubmit} className="space-y-4 h-[70vh] 2xl:h-auto overflow-y-auto vertical">
            {/* Full Name */}
            <div className="text-G300">
              <label className="block text-sm font-normal mb-2">
                Full Name
              </label>
              <input
                type="text"
                name="full_name"
                className="inputtext leading-none"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.full_name}
              />
              {formik.touched.full_name && formik.errors.full_name && (
                <div className="text-red text-sm">
                  {formik.errors.full_name}
                </div>
              )}
            </div>
            <div className="text-G300 ">
              <label className="block text-sm font-normal mb-2">Mobile</label>
              <PhoneInput
                country={'in'}
                value={formik.values.mobile_no}
                onChange={(value) => formik.setFieldValue('mobile_no', value)}
                onBlur={formik.handleBlur}
                disableDropdown
                enableSearch={false}
                countryCodeEditable={false}
                onlyCountries={['in']}
                placeholder="80679 35463"
                inputProps={{
                  name: 'mobile_no',
                  required: true,
                  className: 'w-full inputtext leading-none',
                }}
                buttonStyle={{ display: 'none' }}
              />
              {formik.touched.mobile_no && formik.errors.mobile_no && (
                <div className="text-red text-sm">
                  {formik.errors.mobile_no}
                </div>
              )}
            </div>
            <div className="text-G300">
              <label className="block text-sm font-normal mb-2">Email</label>
              <input
                type="email"
                name="email"
                placeholder="Email"
                className="inputtext leading-none"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.email}
                disabled
              />
              {formik.touched.email && formik.errors.email && (
                <div className="text-red text-sm">{formik.errors.email}</div>
              )}
            </div>
            <div className="text-G300">
              <label className="block text-sm font-normal mb-2">
                Base Salary
              </label>
              <input
                type="number"
                name="base_salary"
                placeholder="Base salary"
                className="inputtext leading-none"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.base_salary}
              />
              {formik.touched.base_salary && formik.errors.base_salary && (
                <div className="text-red text-sm">
                  {formik.errors.base_salary}
                </div>
              )}
            </div>
            <div className="text-G300">
              <label className="block text-sm font-normal mb-2">
                Joining Date
              </label>
              <div className="relative">
                <DatePicker
                  selected={
                    formik.values.joining_date
                      ? new Date(formik.values.joining_date)
                      : null
                  }
                  onChange={(joining_date) =>
                    formik.setFieldValue(
                      "joining_date",
                      joining_date.toLocaleDateString("en-CA")
                    )
                  }
                  placeholderText="yyyy-mm-dd"
                  dateFormat="yyyy-MM-dd"
                  className="inputtext placeholder:text-G50 w-full pr-10 leading-none"
                />
                <icons.MdOutlineCalendarMonth className="absolute right-3 top-3  pointer-events-none text-green" />
              </div>
              {formik.touched.joining_date && formik.errors.joining_date && (
                <div className="text-red text-sm">
                  {formik.errors.joining_date}
                </div>
              )}
            </div>
            <div className="text-G300">
              <label className="block text-sm font-normal mb-2">Role</label>
              <select
                name="role_id"
                className="inputtext"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.role_id}
              >
                <option value="role.id">Select a Role</option>
                {roles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </select>
              {formik.touched.role_id && formik.errors.role_id && (
                <div className="text-red text-sm">{formik.errors.role_id}</div>
              )}
            </div>
            <div className="flex flex-col text-G300 ">
              <label className="block font-normal text-sm mb-2">Location</label>
              <select
                name="location"
                className="inputtext "
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.location}
              >
                <option value="">Select Location</option>
                {location.map((loc) => (
                  <option key={loc.id} value={loc.name}>
                    {loc.name}
                  </option>
                ))}
              </select>
              {formik.touched.location && formik.errors.location && (
                <div className="text-red text-sm">{formik.errors.location}</div>
              )}
            </div>
            <div className="text-G300 relative">
              <label className="block text-sm font-normal mb-2">Password</label>

              <div className="relative w-full">
                <input
                  type={showPassword ? "text" : "password"} // Toggle between text & password
                  name="password"
                  placeholder="Password"
                  className="inputtext w-full pr-10 py-2 pl-3 border border-gray-300 rounded-md" // Ensure spacing & styling
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.password}
                  autoComplete="current-password"
                />
                <span
                  className="absolute inset-y-0 right-3 flex items-center cursor-pointer text-[#0D762A]"
                  onClick={() => setShowPassword(!showPassword)} // Toggle state
                >
                  {showPassword ? (
                    <icons.FaEye className="w-5 h-5" />
                  ) : (
                    <icons.FaEyeSlash className="w-5 h-5" />
                  )}
                </span>
              </div>
              {formik.touched.password && formik.errors.password && (
                <div className="text-red text-sm">{formik.errors.password}</div>
              )}
            </div>

            <div className="text-G300 relative">
              <label className="block text-sm font-normal mb-2">
                Confirm Password
              </label>

              <div className="relative w-full">
                <input
                  type={showConfirmPassword ? "text" : "password"} // Toggle between text & password
                  name="password_confirmation"
                  placeholder="Confirm password"
                  className="inputtext w-full pr-10 py-2 pl-3 border border-gray-300 rounded-md" // Ensure spacing & styling
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.password_confirmation}
                  autoComplete="new-password"
                />

                {/* Eye Icon (Clickable) */}
                <span
                  className="absolute inset-y-0 right-3 flex items-center cursor-pointer text-[#0D762A]"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)} // Toggle state
                >
                  {showConfirmPassword ? (
                    <icons.FaEye className="w-5 h-5" />
                  ) : (
                    <icons.FaEyeSlash className="w-5 h-5" />
                  )}
                </span>
              </div>

              {/* Validation Error Message */}
              {formik.touched.password_confirmation &&
                formik.errors.password_confirmation && (
                  <div className="text-red text-sm">
                    {formik.errors.password_confirmation}
                  </div>
                )}
            </div>

            {/* Submit Button */}
            <div className="flex justify-end space-x-4 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="w-[93px] text-sm py-2 border border-green text-green rounded-lg"
              >
                Close
              </button>
              <button
                type="submit"
                className={`w-[93px] text-sm py-2 rounded-lg ${isUpdating
                  ? "bg-green50 cursor-not-allowed"
                  : "bg-green text-white"
                  }`}
                disabled={isUpdating}
              >
                {isUpdating ? "Updating..." : "Update"}
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  );
};

export default EditStaffForm;
